// src/pages/TeamMeetings.js
// View all team meetings for team leaders and members

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, getDocs
} from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { getMeetingDurationMs } from '../utils/metrics';
import styles from './TeamMeetings.module.css';
import PageLoading from '../components/PageLoading';

function formatDate(timestamp) {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDuration(ms) {
  if (!ms) return '—';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

function MeetingCard({ meeting, onView }) {
  const entryCount = meeting.entries?.length || 0;
  const inventoryScore = meeting.inventoryChecks
    ? Object.values(meeting.inventoryChecks).filter(v => v === true).length
    : 0;
  const isPublished = meeting.status === 'reviewed' || meeting.status === 'completed';

  return (
    <button className={styles.meetingCard} onClick={() => onView(meeting.id)}>
      {/* Name + date */}
      <div className={styles.meetingMain}>
        <div className={styles.meetingName}>{meeting.name}</div>
        <div className={styles.meetingDate}>
          {formatDate(meeting.meetingDate || meeting.createdAt)}
        </div>
      </div>

      {/* Metrics — hidden on mobile */}
      <div className={styles.metricsRow}>
        <div>
          <div className={styles.metricLabel}>Duration</div>
          <div className={styles.metricValue}>{formatDuration(getMeetingDurationMs(meeting))}</div>
        </div>
        <div>
          <div className={styles.metricLabel}>Entries</div>
          <div className={styles.metricValue}>{entryCount}</div>
        </div>
        <div>
          <div className={styles.metricLabel}>Observations</div>
          <div className={styles.metricValue}>{inventoryScore}/40</div>
        </div>
      </div>

      {/* Status + chevron */}
      <div className={styles.meetingRight}>
        <span className={`badge ${isPublished ? 'badge-green' : 'badge-gray'}`}>
          {meeting.status === 'reviewed' ? '✓ Published' : '✓ Completed'}
        </span>
        <span className={styles.meetingChevron}>›</span>
      </div>
    </button>
  );
}

export default function TeamMeetings() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const teamId = userProfile?.teamId;
  const role = userProfile?.role || 'free';

  useEffect(() => {
    if (teamId) loadMeetings();
    else setLoading(false);
  }, [teamId]);

  async function loadMeetings() {
    try {
      console.log('🔍 Loading meetings for team:', teamId);

      const meetingsSnap = await getDocs(
        query(
          collection(db, 'meetings'),
          where('teamId', '==', teamId),
          where('status', 'in', ['completed', 'reviewed'])
        )
      );

      const meetingsList = meetingsSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const dateA = a.meetingDate?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.meetingDate?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA; // Most recent first
        });

      console.log('✓ Found', meetingsList.length, 'meetings');
      setMeetings(meetingsList);
    } catch (err) {
      console.error('❌ Error loading meetings:', err);
    } finally {
      setLoading(false);
    }
  }

  const handleViewMeeting = (meetingId) => {
    navigate(`/meetings/${meetingId}/report`);
  };

  if (loading) {
    return <PageLoading text="Loading meetings..." />;
  }

  if (role === 'free') {
    return (
      <div className="fade-up">
        <div className="page-header">
          <h2>Meetings</h2>
          <p>Only team members and team leaders can view meetings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up">
      <div className="page-header">
        <h2>Team Meetings</h2>
        <p>View all team meetings and consultant reports</p>
      </div>

      {meetings.length === 0 ? (
        <div className="card">
          <p className={styles.emptyText}>
            No meetings recorded yet. Check back after your first meeting with the consultant.
          </p>
        </div>
      ) : (
        <div className={styles.meetingsList}>
          {meetings.map(meeting => (
            <MeetingCard
              key={meeting.id}
              meeting={meeting}
              onView={handleViewMeeting}
            />
          ))}
        </div>
      )}
    </div>
  );
}
